import { useEffect, useRef, useState } from "react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 1.15;

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

export const useZoomPan = ({ imageRef, src }) => {
  const viewportRef = useRef(null);
  const stageRef = useRef(null);
  const panRef = useRef(null);
  const pendingZoomRef = useRef(null);
  const zoomRef = useRef(MIN_ZOOM);
  const isPointerInsideRef = useRef(false);

  const [baseImageSize, setBaseImageSize] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [isPanning, setIsPanning] = useState(false);
  const [isAltPressed, setIsAltPressed] = useState(false);

  const imageSize = baseImageSize ? { width: baseImageSize.width * zoom, height: baseImageSize.height * zoom } : null;
  const canvasWidth = imageSize ? Math.max(imageSize.width, viewportSize.width) : viewportSize.width;
  const canvasHeight = imageSize ? Math.max(imageSize.height, viewportSize.height) : viewportSize.height;
  const stageLeft = imageSize ? Math.max((viewportSize.width - imageSize.width) / 2, 0) : 0;
  const stageTop = imageSize ? Math.max((viewportSize.height - imageSize.height) / 2, 0) : 0;

  const updateSizes = () => {
    const viewport = viewportRef.current;
    const image = imageRef.current;

    if (!viewport) return;

    const width = viewport.clientWidth;
    const height = viewport.clientHeight;

    setViewportSize({ width, height });

    if (!image || !image.naturalWidth || !image.naturalHeight || !width || !height) return;

    const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);

    setBaseImageSize({
      width: image.naturalWidth * scale,
      height: image.naturalHeight * scale,
    });
  };

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    setBaseImageSize(null);
    setZoom(MIN_ZOOM);
    setIsAltPressed(false);
    zoomRef.current = MIN_ZOOM;
    panRef.current = null;
    pendingZoomRef.current = null;
    setIsPanning(false);

    requestAnimationFrame(() => {
      if (!viewportRef.current) return;

      viewportRef.current.scrollLeft = 0;
      viewportRef.current.scrollTop = 0;
    });
  }, [src]);

  useEffect(() => {
    updateSizes();

    const resizeObserver = new ResizeObserver(updateSizes);

    if (viewportRef.current) resizeObserver.observe(viewportRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const stage = stageRef.current;
      if (!stage) return;

      const stageRect = stage.getBoundingClientRect();

      const isInsideStage = event.clientX >= stageRect.left && event.clientX <= stageRect.right &&
        event.clientY >= stageRect.top && event.clientY <= stageRect.bottom;

      if (!isInsideStage) return;

      const currentZoom = zoomRef.current;
      const nextZoom = event.deltaY < 0 ? Math.min(currentZoom * ZOOM_STEP, MAX_ZOOM) : Math.max(currentZoom / ZOOM_STEP, MIN_ZOOM);

      if (nextZoom === currentZoom) return;

      pendingZoomRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
        relativeX: (event.clientX - stageRect.left) / stageRect.width,
        relativeY: (event.clientY - stageRect.top) / stageRect.height,
      };

      zoomRef.current = nextZoom;
      setZoom(nextZoom);
    };

    viewport.addEventListener('wheel', handleWheel, { passive: false });

    return () => viewport.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Alt' || !isPointerInsideRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      setIsAltPressed(true);
    };

    const handleKeyUp = (event) => {
      if (event.key !== 'Alt') return;

      if (isPointerInsideRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }

      setIsAltPressed(false);
    };

    const handleWindowBlur = () => {
      setIsAltPressed(false);
      setIsPanning(false);
      panRef.current = null;
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    if (zoom === MIN_ZOOM) {
      pendingZoomRef.current = null;

      requestAnimationFrame(() => {
        if (!viewportRef.current) return;

        viewportRef.current.scrollLeft = 0;
        viewportRef.current.scrollTop = 0;
      });

      return;
    }

    const pendingZoom = pendingZoomRef.current;
    if (!pendingZoom || !stageRef.current) return;

    pendingZoomRef.current = null;

    requestAnimationFrame(() => {
      const currentViewport = viewportRef.current;
      const stage = stageRef.current;

      if (!currentViewport || !stage) return;

      const stageRect = stage.getBoundingClientRect();
      const targetX = stageRect.left + pendingZoom.relativeX * stageRect.width;
      const targetY = stageRect.top + pendingZoom.relativeY * stageRect.height;

      currentViewport.scrollLeft += targetX - pendingZoom.clientX;
      currentViewport.scrollTop += targetY - pendingZoom.clientY;
    });
  }, [zoom]);

  const handlePointerEnter = (event) => {
    isPointerInsideRef.current = true;
    setIsAltPressed(event.altKey);
  };

  const handlePointerLeave = () => {
    isPointerInsideRef.current = false;

    if (!panRef.current) setIsAltPressed(false);
  };

  const handlePanPointerDown = (event) => {
    if (!event.altKey || zoomRef.current <= MIN_ZOOM || !viewportRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    viewportRef.current.setPointerCapture(event.pointerId);

    panRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: viewportRef.current.scrollLeft,
      scrollTop: viewportRef.current.scrollTop,
    };

    setIsPanning(true);
  };

  const handlePanPointerMove = (event) => {
    if (!panRef.current || !viewportRef.current) return;

    event.preventDefault();

    viewportRef.current.scrollLeft = panRef.current.scrollLeft - (event.clientX - panRef.current.clientX);
    viewportRef.current.scrollTop = panRef.current.scrollTop - (event.clientY - panRef.current.clientY);
  };

  const handlePanPointerUp = (event) => {
    if (!panRef.current) return;

    if (viewportRef.current?.hasPointerCapture(event.pointerId)) viewportRef.current.releasePointerCapture(event.pointerId);

    panRef.current = null;
    setIsPanning(false);
  };

  return {
    viewportRef,
    stageRef,
    zoom,
    isPanning,
    isAltPressed,
    imageSize,
    canvasWidth,
    canvasHeight,
    stageLeft,
    stageTop,
    updateSizes,
    handlePointerEnter,
    handlePointerLeave,
    handlePanPointerDown,
    handlePanPointerMove,
    handlePanPointerUp,
  };
};